import { BusinessClock } from '../../../../core/time/business-clock.service';
import { clockView } from '../../../../core/time/business-time';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { SfappService } from '../../../../core/services/sfapp.service';
import { MemberStateService } from '../../application/member-state.service';
import { FormatDatePipe } from '../../../../shared/pipes/format-date.pipe';
import { getSelectColumnName, normalizeQuestionaries } from '../../../../shared/utils/member-helpers';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RegistrationWorkflow } from '../../application/registration-workflow.service';
import { RegistrationKind, firstInvoiceDate } from '../../domain/registration-command';
import { FileModelDirective } from '../../../../shared/directives/file-model.directive';

@Component({
    selector: 'app-step3',
    imports: [CommonModule, TranslatePipe, FormatDatePipe, CurrencyPipe, DatePipe, FileModelDirective],
    templateUrl: './step3.component.html'
})
export class Step3Component implements OnInit, OnDestroy {
  private readonly businessClock = inject(BusinessClock);
  private readonly clockContext = clockView(false);

  readonly workflow = inject(RegistrationWorkflow);
  private alive = true;
  private invoiceTimer?: ReturnType<typeof setTimeout>;
  private readers = new Set<FileReader>();
  private imageVersions = { 1: 0, 2: 0 };
  private sfapp = inject(SfappService);
  private stateService = inject(MemberStateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private sanitizer = inject(DomSanitizer);

  state = this.stateService.get();
  get member() { return this.state.member; }

  firstInvoiceDate: Date | null = null;
  firstInvoiceElement = '';
  uploadImg: { img1?: File | null; img2?: File | null; imgUri1?: string | null; imgUri2?: string | null } = {};
  noteHtml: SafeHtml = '';
  questionaryList: any[] = [];
  btPaymentEnabled = 0;
  getSelectColumnName = getSelectColumnName;

  async ngOnInit(): Promise<void> {
    const completed = this.workflow.resume() ?? this.stateService.completedRegistration;
    if (completed) {
      await this.router.navigate(['/step4', completed.type, completed.studioId], { replaceUrl: true });
      return;
    }
    if (this.workflow.message) return;
    if (!this.member.visitors && !this.member.studioPlanId) {
      this.router.navigate(['/step1']);
      return;
    }
    this.stateService.setShowConfirm(true);
    this.stateService.setShowResult(false);
    this.noteHtml = this.sanitizer.bypassSecurityTrustHtml(this.member.tmp.note ?? '');
    normalizeQuestionaries(this.member.tmp?.questionarys);
    this.questionaryList = this.toQuestionaryList(this.member.tmp?.questionarys);
    const tmpBtPaymentEnabled = this.member.tmp?.btPaymentEnabled;
    this.btPaymentEnabled = tmpBtPaymentEnabled === undefined ? 0 : Number(tmpBtPaymentEnabled || 0);
    if (tmpBtPaymentEnabled === undefined && this.member.studioId) {
      const studioInfo = await this.sfapp.isDisplayed(this.member.studioId);
      this.btPaymentEnabled = Number(studioInfo?.btPaymentEnabled || 0);
      this.member.tmp.btPaymentEnabled = this.btPaymentEnabled;
      this.member.btPaymentEnabled = this.btPaymentEnabled;
    }

    if (this.member.desiredStartDate) {
      const result = await this.sfapp.getDate();
      if (result) {
        this.firstInvoiceDate = firstInvoiceDate(this.member.desiredStartDate.id, result.arr);
        this.invoiceTimer = setTimeout(() => {
          if (!this.alive) return;
          this.firstInvoiceElement = document.getElementById('studio_plan_info')?.innerHTML ?? '';
        }, 500);
      }
    }
  }

  private toQuestionaryList(questionarys: any): any[] {
    if (!questionarys) return [];
    return Array.isArray(questionarys) ? questionarys : Object.values(questionarys);
  }

  onFileChange(file: File | null, index: 1 | 2): void {
    const version = ++this.imageVersions[index];
    if (!file) {
      if (index === 1) { this.uploadImg.img1 = null; this.uploadImg.imgUri1 = null; }
      else { this.uploadImg.img2 = null; this.uploadImg.imgUri2 = null; }
      return;
    }
    if (index === 1) this.uploadImg.img1 = file;
    else this.uploadImg.img2 = file;
    const reader = new FileReader();
    this.readers.add(reader);
    reader.onloadend = () => this.readers.delete(reader);
    reader.onload = (e) => {
      if (!this.alive || this.imageVersions[index] !== version) return;
      if (index === 1) { this.uploadImg.img1 = file; this.uploadImg.imgUri1 = e.target?.result as string; }
      else { this.uploadImg.img2 = file; this.uploadImg.imgUri2 = e.target?.result as string; }
    };
    reader.readAsDataURL(file);
  }

  back(): void {
    if (!this.state.showProcess) this.router.navigate(this.state.inStudio && this.member.tmp.studio
      ? ['/in_studio', this.member.tmp.studio[0], this.member.tmp.studio[1].trim()] : ['/step2']);
  }
  goBack(): void {
    if (!this.state.showProcess) window.history.back();
  }

  ngOnDestroy(): void {
    this.alive = false;
    if (this.invoiceTimer) clearTimeout(this.invoiceTimer);
    this.readers.forEach(reader => reader.abort()); this.readers.clear();
    this.uploadImg = {};
  }

  save(): Promise<void> { return this.submit('new_membership'); }
  saveVisitors(): Promise<void> { return this.submit('temporary_registration'); }
  register(): Promise<void> { return this.submit('main_registration'); }

  private async submit(kind: RegistrationKind): Promise<void> {
    if (!await this.businessClock.confirm(this.clockContext)) return;
    const result = await this.workflow.submit(kind, {
      clockPeriod: this.clockContext.period,
      invoiceDate: this.firstInvoiceDate,
      invoiceHtml: document.getElementById('studio_plan_info')?.innerHTML || this.firstInvoiceElement,
      hasImages: !!(this.uploadImg.img1 || this.uploadImg.img2), inStudio: this.state.inStudio,
    }, [this.uploadImg.img1, this.uploadImg.img2]);
    if (result.kind !== 'blocked') { this.uploadImg = {}; ++this.imageVersions[1]; ++this.imageVersions[2]; }
    if (!this.alive) return;
    if (result.kind === 'expired') {
      this.toastr.error('見学希望日の有効期限が切れました。', 'エラー');
      await this.router.navigate(['/step2']);
    } else if (result.kind === 'result') {
      await this.router.navigate(['/step4', result.destination.type, result.destination.studioId]);
    }
  }
}
